import apiClient from '@/lib/api/client';
import { API_ENDPOINTS } from '@/constants/api';
import type { CreateFeedbackRequest, FeedbackCategory, FeedbackResponse } from '../types';

/**
 * Feedback Service
 * API calls for submitting feedback and fetching categories.
 * Last Updated Date: July 16, 2026
 * @author Jette
 */

export const feedbackService = {
  /**
   * DOCU: Fetch the active feedback categories from the server. <br>
   * Triggered: FeedbackDialog via useFeedbackCategories. <br>
   * Last Updated Date: July 16, 2026
   * @returns Active feedback categories, ordered for display
   * @author Jette
   */
  async getCategories(): Promise<FeedbackCategory[]> {
    const response = await apiClient.get<{
      code: string;
      result: FeedbackCategory[];
    }>(API_ENDPOINTS.FEEDBACK_CATEGORIES);
    return response.data.result;
  },

  /**
   * DOCU: Submit a new feedback entry with optional image attachments. <br>
   * Triggered: FeedbackDialog form submit via useSubmitFeedback. <br>
   * Last Updated Date: August 13, 2026
   * @param data - Feedback payload (category_id, description, optional page_url, optional images)
   * @returns The created feedback record
   * @author Jette
   */
  async submitFeedback(data: CreateFeedbackRequest): Promise<FeedbackResponse> {
    const form_data = new FormData();
    form_data.append('category_id', String(data.category_id));
    form_data.append('description', data.description);
    if (data.page_url) form_data.append('page_url', data.page_url);
    if (data.images) {
      data.images.forEach(file => form_data.append('images', file));
    }
    const response = await apiClient.post<{
      code: string;
      result: FeedbackResponse;
    }>(API_ENDPOINTS.FEEDBACK, form_data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.result;
  },
};
